/*
  <megamenu-media> — defers mega-menu pin images until the shopper opens the menu.

  WHY A TEMPLATE AND NOT loading="lazy":
  snippets/halo-megamenu.liquid renders its dropdown inside .menu-dropdown, which
  component-megamenu.css hides with `visibility:hidden` (not `display:none`) and positions
  absolutely under the header. A visibility:hidden element still generates layout boxes, and
  that box sits inside the viewport - so both native lazy-loading and lazysizes consider the
  images "visible" and fetch them during initial page load. `loading="lazy"` is therefore
  unreliable here.

  <template> contents live in an inert document fragment. Per spec the browser does not fetch
  resources inside it, so the initial page load carries ZERO fetchable image URLs for these
  pins. That is a guarantee rather than a heuristic.

  Follows the pattern already used in this theme:
    assets/deferred-media.js:9        template.content.cloneNode(true) on click
    assets/theme.js:1011              mobile nav, template.content.firstElementChild
    assets/pickup-availability.js:10  template.content.firstElementChild.cloneNode(true)
*/
if (!customElements.get('megamenu-media')) {
    const MEGAMENU_MEDIA_EVENTS = ['mouseenter', 'focusin', 'touchstart'];

    class MegamenuMedia extends HTMLElement {
        connectedCallback() {
            if (this.hasAttribute('loaded')) return;

            // The dropdown is opened by CSS :hover on this ancestor, so it is the correct
            // place to listen. focusin covers keyboard navigation, touchstart covers touch
            // devices that reach the desktop menu.
            const trigger = this.closest('.menu-lv-item.has-megamenu');

            // No recognisable trigger (markup changed, or rendered outside the menu):
            // fail OPEN and show the images immediately rather than hiding them forever.
            if (!trigger) {
                this.activate();
                return;
            }

            // One listener set per menu item, not per image. A sibling <megamenu-media> in
            // the same dropdown may already have wired it; that listener activates every
            // descendant, so binding again would only duplicate work.
            if (trigger.dataset.megamenuMediaWired) return;
            trigger.dataset.megamenuMediaWired = 'true';

            const activateAll = () => {
                trigger.querySelectorAll('megamenu-media').forEach((el) => {
                    if (typeof el.activate === 'function') el.activate();
                });
                MEGAMENU_MEDIA_EVENTS.forEach((ev) => trigger.removeEventListener(ev, activateAll));
            };

            MEGAMENU_MEDIA_EVENTS.forEach((ev) => {
                trigger.addEventListener(ev, activateAll, { passive: true });
            });
        }

        activate() {
            // Clone once. Re-opening the menu must not clone or refetch anything.
            if (this.hasAttribute('loaded')) return;
            this.setAttribute('loaded', '');

            const template = this.querySelector('template');
            if (!template) return;

            this.appendChild(template.content.cloneNode(true));
            template.remove();
        }
    }

    customElements.define('megamenu-media', MegamenuMedia);
}
